import axios from 'axios';

// Get backend URL from environment
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
console.log('API Base URL:', API_BASE_URL);
// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const getToken = () => localStorage.getItem('token');
const setToken = (token) => localStorage.setItem('token', token);
const removeToken = () => localStorage.removeItem('token');

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// AUTH API
// ============================================================================

export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    if (response.data.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  // Login user
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    if (response.data.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  // Get current user info
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Logout
  logout: () => {
    removeToken();
  }
};

// ============================================================================
// TRAINER PROFILE API
// ============================================================================

export const trainerAPI = {
  // Create trainer profile
  createProfile: async (profileData) => {
    const response = await api.post('/api/trainer/profile', profileData);
    return response.data;
  },

  // Get trainer profile
  getProfile: async () => {
    const response = await api.get('/api/trainer/profile');
    return response.data;
  },

  // Update trainer profile
  updateProfile: async (profileData) => {
    const response = await api.put('/api/trainer/profile', profileData);
    return response.data;
  },

  // Get trainer materials
  getMaterials: async (category = null) => {
    const params = category ? { category } : {};
    const response = await api.get('/api/materials', { params });
    return response.data;
  },

  // Create training material
  createMaterial: async (materialData) => {
    const response = await api.post('/api/materials', materialData);
    return response.data;
  },

  // Get client contacts
  getContacts: async () => {
    const response = await api.get('/api/contacts');
    return response.data;
  },

  // Create client contact
  createContact: async (contactData) => {
    const response = await api.post('/api/contacts', contactData);
    return response.data;
  },

  // Get trainer proposals
  getProposals: async () => {
    const response = await api.get('/api/proposals');
    return response.data;
  },

  // Create proposal
  createProposal: async (proposalData) => {
    const response = await api.post('/api/proposals', proposalData);
    return response.data;
  },

  // Get trainer bookings
  getBookings: async () => {
    const response = await api.get('/api/bookings');
    return response.data;
  }
};

// ============================================================================
// ORGANIZATION PROFILE API
// ============================================================================

export const organizationAPI = {
  // Create organization profile
  createProfile: async (profileData) => {
    const response = await api.post('/api/organization/profile', profileData);
    return response.data;
  },

  // Get organization profile
  getProfile: async () => {
    const response = await api.get('/api/organization/profile');
    return response.data;
  },

  // Create training requirement
  createRequirement: async (requirementData) => {
    const response = await api.post('/api/requirements', requirementData);
    return response.data;
  },

  // Get training requirements
  getRequirements: async () => {
    const response = await api.get('/api/requirements');
    return response.data;
  },

  // Get specific requirement
  getRequirement: async (requirementId) => {
    const response = await api.get(`/api/requirements/${requirementId}`);
    return response.data;
  },

  // Get received proposals
  getProposals: async () => {
    const response = await api.get('/api/proposals');
    return response.data;
  },

  // Update proposal status
  updateProposalStatus: async (proposalId, status) => {
    const response = await api.put(`/api/proposals/${proposalId}/status`, { status });
    return response.data;
  },

  // Get organization bookings
  getBookings: async () => {
    const response = await api.get('/api/bookings');
    return response.data;
  },

  // Create booking
  createBooking: async (bookingData) => {
    const response = await api.post('/api/bookings', bookingData);
    return response.data;
  }
};

// ============================================================================
// GENERAL API
// ============================================================================

export const generalAPI = {
  // Get dashboard statistics
  getDashboardStats: async () => {
    const response = await api.get('/api/dashboard/stats');
    return response.data;
  },

  // Get training requirements (for trainers to browse)
  getBrowseRequirements: async () => {
    const response = await api.get('/api/requirements');
    return response.data;
  },

  // Send message
  sendMessage: async (messageData) => {
    const response = await api.post('/api/messages', messageData);
    return response.data;
  },

  // Get messages
  getMessages: async () => {
    const response = await api.get('/api/messages');
    return response.data;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const apiUtils = {
  // Check if user is authenticated
  isAuthenticated: () => {
    return !!getToken();
  },

  // Get stored token
  getToken,

  // Set token
  setToken,

  // Remove token
  removeToken,

  // Handle API errors
  handleApiError: (error) => {
    if (error.response) {
      // Server responded with error
      return error.response.data.detail || 'An error occurred';
    } else if (error.request) {
      // No response received
      return 'Unable to connect to server';
    } else {
      // Request setup error
      return 'Request failed';
    }
  }
};

export default api;